/**
 * Review aggregation helpers.
 *
 * Phase 1 (launch): we compute a user's average rating on the fly from
 * the reviews already fetched via `sdk.reviews.query({ subject_id })`.
 * Volume is low, so averaging the loaded array is fine.
 *
 * TODO: migrate to a cached `publicData.ratingAverage` /
 * `publicData.ratingCount` written server-side when a review is
 * published, once sellers start accumulating enough reviews that the
 * on-the-fly query/paging (100/page max) becomes a concern. Both the
 * SellerCard and ProfilePage would then just read the cached numbers.
 */

/**
 * Compute the average rating + count from a list of review entities.
 *
 * @param {Array} reviews - denormalised review entities, each with
 *   `attributes.rating` (integer 1–5). Entries without a numeric rating
 *   (e.g. a review left without a star score) are ignored in the average.
 * @returns {{ average: number, count: number, hasReviews: boolean }}
 *   `average` is 0 when there are no rated reviews; `count` is the number
 *   of rated reviews.
 */
export const getRatingStats = (reviews = []) => {
  const rated = (Array.isArray(reviews) ? reviews : []).filter(
    r => typeof r?.attributes?.rating === 'number'
  );
  const count = rated.length;
  if (count === 0) {
    return { average: 0, count: 0, hasReviews: false };
  }
  const sum = rated.reduce((acc, r) => acc + r.attributes.rating, 0);
  return { average: sum / count, count, hasReviews: true };
};
